import { IConversationBulkAction, IConversationUpdate } from '../models'
import servicesConfig, { CustomAxiosHeaders } from '../config/services'
import { requests } from '.'
import { AxiosResponse } from 'axios'
import { NEW_MESSAGE_FLOW_CONVERSATION_ID } from '../util/constants'

declare enum AuditVoteType {
  UP = 'up',
  DOWN = 'down'
}

interface WhatsAppLocationSetupBody {
  companyHQCountry: string
  companyName: string
  companyVertical: string
  companyWebsite: string
  facebookBusinessManagerId: string
  numOfMessages: string
  regionSendingTo: string
  twilioAccountSID: string
  useCase: string
  location: string
}

interface WhatsAppNumberSetupBody {
  locationId: string,
  number: string,
  displayName: string,
  address?: string,
  companyEmail?: string,
  website?: string,
  logo?: string,
}

export interface CheckForSpamBody {
  text: string
  locationId: string
  contactId?: string
}

export interface UpdateConversationBody{
  thumbVote?: AuditVoteType
  attachmentsSyncStatus?:{
    url:string
    isSynced:boolean
  }[]
}

export interface InboundMessageSuggestion{
  thumbVote: AuditVoteType,
  conversationId: string,
  locationId: string
}

// Configure request setup
const serviceName = 'ConversationsService'
const host = servicesConfig.Conversations
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

// Define service object with methods
const ConversationsService = {
  message: (body: any) => 
    request().post('/messages', body).then(responseBody),
    
  getMessageByAltId: (altId: string) => 
    request().get(`/messages/search?altId=${altId}`).then(responseBody),
    
  // WhatsApp Location Setup APIs
  getWhatsAppLocationSetupStatus: (locationId: string) =>
    request().get(
      `/providers/whatsapp/location/${locationId}/status`
    ).then(responseBody),
    
  postWhatsAppLocationSetupRequest: (body: WhatsAppLocationSetupBody) =>
    request().post(
      '/providers/whatsapp/location/setup',
      body
    ).then(responseBody),
    
  getWhatsAppNumbers: (locationId: string) =>
    request().get(
      `/providers/whatsapp/${locationId}/numbers`
    ).then(responseBody),
    
  postWhatsAppNumberSetupRequest: (body: WhatsAppNumberSetupBody) =>
    request().post(
      '/providers/whatsapp/number/setup',
      body
    ).then(responseBody),
    
  enableWhatsAppNumber: (locationId: string, number: string) =>
    request().put(`/providers/whatsapp/number/state`, {
      state: "enable",
      number,
      locationId
    }).then(responseBody),

  getById: (conversationId: string) => 
    request().get(`/${conversationId}`).then(responseBody),
    
  getConversationsV2: (filterString: string) => 
    request().get(`/search?${filterString}`).then(responseBody),
    
  performBulkActions: (data: IConversationBulkAction) => 
    request().post(`/bulk`, data).then(responseBody),
    
  updateConversation: (id: string, data: IConversationUpdate) => 
    request().put(`/${id}`, data).then(responseBody),

  updateFilters: async (userId: string, locationId: string, filters: any) => {
    const {
      leftPanel: {
        assignedTo,
        followers,
        lastMessageType,
        lastMessageDirection,
        lastMessageAction,
        sortScoreProfile,
        scoreProfile,
        scoreProfileMin,
        scoreProfileMax,
        sort,
        sortBy,
        status,
      },
    } = filters

    const reqBody = {
      conversations: {
        leftPanel: {
          assignedTo,
          followers,
          lastMessageType,
          lastMessageDirection,
          lastMessageAction,
          sortScoreProfile,
          scoreProfile,
          scoreProfileMin,
          scoreProfileMax,
          sort,
          sortBy,
          status
        }
      }
    }
    return request().put(`/filters?userId=${userId}&locationId=${locationId}`, reqBody).then(responseBody)
  },

  fetchFilters: (userId: string, locationId: string) =>
    request().get(`/filters?userId=${userId}&locationId=${locationId}`).then(responseBody),

  // email/sms usage stats
  getMessageUsageStats: (locationId: string, type: string) =>
    request().get(`/messages/${locationId}/${type}/usage`).then(responseBody),

  updateMessageUsageStats: (locationId: string, type: string, newLimit: string) =>
    request().put(`/messages/${locationId}/${type}/usage?newLimit=${newLimit}`, {}).then(responseBody),

  getMessages: (threadID: string) =>
    request().get(
      `/messages/email/search?threadId=${threadID}&deleted=false`
    ).then(responseBody),
    
  getThread: (threadID: string) => 
    request().get(`/messages/${threadID}`).then(responseBody),
    
  getThreadEmailMsg: (emailMessageId: string) => 
    request().get(`/messages/email/${emailMessageId}`).then(responseBody),
    
  getRateLimit: (locationId: string) => 
    request().get(`/messages/${locationId}/rate-limit/twilio`, {}).then(responseBody),

  fetchDomainsByQuery: (params: any) =>
    request().get(`/providers/mailgun/domains`, { params })
      .then(data => ({ data }))
      .catch((error) => error?.response),

  cancelScheduledMessage: (data: { email: boolean, id: string }) => {
    if (data.email) {
      return request().delete(`/messages/email/${data.id}/schedule`).then(responseBody)
    }
    return request().delete(`/messages/${data.id}/schedule`).then(responseBody)
  },
  
  getMessagesForConversation: (conversationId: string, params: any) => {
    if (conversationId === NEW_MESSAGE_FLOW_CONVERSATION_ID) {
      return {
        messages: [],
        nextPage: false
      }
    }
    return request().get(`/${conversationId}/messages`, { params }).then(responseBody)
  },

  checkForSpam: (body: CheckForSpamBody): Promise<{ isSpam: boolean }> => {
    return request().post(
      '/messages/spam/v2',
      body
    ).then(responseBody)
  },
  
  deleteConversation: (conversationId: string) => 
    request().delete(`/${conversationId}`).then(responseBody),

  updateMessage: (messageId: string, body: UpdateConversationBody) =>
    request().put(`/messages/${messageId}`, body).then(responseBody),

  updateInboundMessageSuggestion: (suggestionId: string, body: InboundMessageSuggestion) =>
    request().put(`/inbound/message/suggestion/${suggestionId}`, body).then(responseBody),

  recording: (locationId: string, messageId: string, index: number) => {
    return request().get(
      `/messages/${messageId}/locations/${locationId}/recording?index=${index}`, {
        responseType: 'blob'
      }
    ).then(responseBody)
  },

  requestContactDetails: (body: any) => {
    return request().post(`/providers/live-chat/user/request/contact`,
      body
    ).then(responseBody)
  },

  endLiveChat: (body: any) => {
    return request().post(`/providers/live-chat/user/close`,
      body
    ).then(responseBody)
  },
  
  getMessageById: (messageId: string) => 
    request().get(`/messages/${messageId}`).then(responseBody),
    
  transcription: (locationId: string, messageId: string, index: number) => {
    return request().get(
      `/locations/${locationId}/messages/${messageId}/transcription?index=${index}`
    ).then(responseBody)
  },

  getDraftMessages: (locationId: string, contactId: string) => {
    return request().get(`/messages/drafts/${locationId}/contacts/${contactId}`).then(responseBody)
  },

  saveDraftMessage: (body: any) => {
    return request().post(`/messages/drafts`, body).then(responseBody)
  },

  deleteDraftMessageById: (draftId: string) => {
    return request().delete(`/messages/drafts/${draftId}`).then(responseBody)
  },

  deleteDraftMessageByContactChannel: (locationId: string, contactId: string, channel: string, providerId?: string) => {
    if (!contactId) return
    let url = `/messages/drafts/${locationId}/contacts/${contactId}/${channel}`
    if (providerId) url += `/${providerId}`
    return request().delete(url).then(responseBody)
  },

  deleteInternalComment: (commentId: string) => {
    return request().delete(`/messages/internal-comment/${commentId}`).then(responseBody)
  }
}

export default ConversationsService
